import { apiFetch } from './client';
import { useAuth } from '@/lib/auth-store';

export interface CallSessionRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  direction: 'inbound' | 'outbound';
  freeswitch_uuid: string | null;
  from_number: string | null;
  to_number: string | null;
  did_id: string | null;
  tree_id: string | null;
  current_node_id: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_sec: number | null;
  hangup_cause: string | null;
  keys_pressed: string | null;
  cost_cents: number;
  /** CNAM-resolved display name for the caller (migration 0088). Null
   *  when the cache is missing/stale OR the caller is anonymous.
   *  Renderers should prefer this over from_number when set. */
  caller_display_name: string | null;
  /** Tree name joined server-side. Present on LIST rows only (the
   *  detail endpoint omits it); null when the session has no tree. */
  tree_name?: string | null;
}

export interface CallEventRow {
  id: string;
  session_id: string;
  node_id: string | null;
  event_kind: string;
  dtmf: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ListCallSessionsQuery {
  from?: string;
  to?: string;
  tree_id?: string;
  search?: string;
  /** Targeted ILIKE filter on from_number (caller's E.164). */
  from_number?: string;
  /** Targeted ILIKE filter on to_number (the dialed DID). */
  to_number?: string;
  /** Call-type filter: inbound / outbound / missed. */
  direction?: 'inbound' | 'outbound' | 'missed';
  /** Live calls only (ended_at IS NULL). Dashboard "Live now" panel. */
  active?: boolean;
  limit?: number;
  offset?: number;
  customer_id?: string;
}

function buildQueryString(p: ListCallSessionsQuery): URLSearchParams {
  const q = new URLSearchParams();
  if (p.from) q.set('from', p.from);
  if (p.to) q.set('to', p.to);
  if (p.tree_id) q.set('tree_id', p.tree_id);
  if (p.search) q.set('search', p.search);
  if (p.from_number) q.set('from_number', p.from_number);
  if (p.to_number) q.set('to_number', p.to_number);
  if (p.direction) q.set('direction', p.direction);
  if (p.active) q.set('active', 'true');
  if (typeof p.limit === 'number') q.set('limit', String(p.limit));
  if (typeof p.offset === 'number') q.set('offset', String(p.offset));
  if (p.customer_id) q.set('customer_id', p.customer_id);
  return q;
}

export const callSessions = {
  list: (p: ListCallSessionsQuery = {}) => {
    const q = buildQueryString(p);
    return apiFetch<{ rows: CallSessionRow[]; total: number }>(`/api/call-sessions?${q}`);
  },
  get: (id: string) =>
    apiFetch<{ session: CallSessionRow; events: CallEventRow[] }>(
      `/api/call-sessions/${id}`,
    ),
  /**
   * Download the CSV export as a Blob. We fetch through the auth-aware
   * apiFetch path (Bearer token in header) and synthesise a download
   * via an object URL — a plain `<a href download>` would skip the
   * Authorization header and fail with 401 since the API doesn't
   * accept cookie-based auth on this route.
   */
  exportCsv: async (p: ListCallSessionsQuery = {}): Promise<void> => {
    const q = buildQueryString(p);
    const { accessToken } = useAuth.getState();
    const res = await fetch(`/api/call-sessions/export.csv?${q}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `call-log-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
